import React, { useEffect, useState } from "react";
import { AuthError } from "../../data/errors.js";
import {
  getUserLoginHistory,
  getUserRecentActivity,
} from "../../data/usageAnalyticsClient.js";
import { T } from "../../app/tokens.js";

function formatDate(value) {
  if (!value) return "No activity yet";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "Unknown"
    : date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

export function AdminUserActivityDrawer({ user, onClose }) {
  const [state, setState] = useState({ loading: true, error: null, events: [] });
  const [loginPage, setLoginPage] = useState(1);
  const [loginState, setLoginState] = useState({ loading: true, error: null, data: null });

  useEffect(() => {
    let cancelled = false;
    getUserRecentActivity(user.id)
      .then((data) => {
        if (!cancelled) setState({ loading: false, error: null, events: data.events || [] });
      })
      .catch((error) => {
        if (!cancelled) setState({ loading: false, error, events: [] });
      });
    return () => { cancelled = true; };
  }, [user.id]);

  useEffect(() => {
    let cancelled = false;
    setLoginState((current) => ({ ...current, loading: true, error: null }));
    getUserLoginHistory(user.id, { page: loginPage, pageSize: 20 })
      .then((data) => {
        if (!cancelled) setLoginState({ loading: false, error: null, data });
      })
      .catch((error) => {
        if (!cancelled) setLoginState({ loading: false, error, data: null });
      });
    return () => { cancelled = true; };
  }, [user.id, loginPage]);

  return (
    <div
      data-testid="admin-user-activity-drawer"
      role="dialog"
      data-feedback-obscuring-overlay="true"
      aria-label={`Recent activity for ${user.displayName}`}
      style={{ position: "fixed", inset: 0, zIndex: 20, background: "rgba(16, 30, 45, .28)" }}
      onClick={onClose}
    >
      <aside
        style={{ marginLeft: "auto", height: "100%", width: "min(420px, 92vw)", background: T.white, boxShadow: "0 8px 28px rgba(16,30,45,.18)", padding: 24, overflowY: "auto" }}
        onClick={(event) => event.stopPropagation()}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", gap: 16 }}>
          <div>
            <h3 data-testid="admin-user-drawer-name" style={{ margin: 0, color: T.dark, fontSize: T.fsLg }}>{user.displayName || user.email || "User"}</h3>
            <p style={{ margin: "5px 0 0", color: T.mid, fontSize: T.fsXs }}>{user.email} · {user.role}</p>
          </div>
          <button type="button" data-testid="admin-user-drawer-close" onClick={onClose} aria-label="Close user activity">Close</button>
        </div>
        <div style={{ marginTop: 20, display: "grid", gap: 8, color: T.mid, fontSize: T.fsSm }}>
          <div><strong style={{ color: T.dark }}>Status:</strong> {user.status}</div>
          <div><strong style={{ color: T.dark }}>Last active:</strong> {formatDate(user.lastActiveAt)}</div>
          <div><strong style={{ color: T.dark }}>Okta groups:</strong> {(user.oktaGroups || []).join(", ") || "None captured"}</div>
        </div>
        <div style={{ display: "flex", gap: 14, marginTop: 16, fontSize: T.fsSm }}>
          <a data-testid="admin-user-drawer-activity-link" href={`/admin?section=activity&userId=${encodeURIComponent(user.id)}`} style={{ color: T.blue }}>Open Activity view</a>
          <a data-testid="admin-user-drawer-role-link" href={`/admin?section=roles&role=${encodeURIComponent(user.role)}`} style={{ color: T.blue }}>Open role permissions</a>
        </div>
        <h4 style={{ color: T.dark, margin: "28px 0 12px", fontSize: T.fsBase }}>Login history</h4>
        <p style={{ margin: "0 0 12px", color: T.mid, fontSize: T.fsXs }}>
          Retained successful sign-ins only; older sign-ins are not reconstructed.
        </p>
        {loginState.loading && <p data-testid="admin-user-login-loading" style={{ color: T.mid }}>Loading login history…</p>}
        {loginState.error && <p data-testid="admin-user-login-error" role="alert" style={{ color: T.red }}>{loginState.error instanceof AuthError ? "You are not allowed to view this login history." : "Login history could not be loaded."}</p>}
        {!loginState.loading && !loginState.error && (loginState.data?.logins || []).length === 0 && (
          <p data-testid="admin-user-login-empty" style={{ color: T.mid }}>No captured logins for this user.</p>
        )}
        {!loginState.loading && !loginState.error && (loginState.data?.logins || []).length > 0 && (
          <>
            <ul data-testid="admin-user-login-list" style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {loginState.data.logins.map((login) => (
                <li key={login.id} data-testid="admin-user-login-row" style={{ padding: "12px 0", borderBottom: `1px solid ${T.border}` }}>
                  <div style={{ color: T.dark, fontSize: T.fsSm, fontWeight: T.fwSemibold }}>{formatDate(login.occurredAt)}</div>
                  <div style={{ color: T.mid, fontSize: T.fsXs, marginTop: 4 }}>
                    {login.authMethod || "Authentication not recorded"} · {login.environment || "Environment not recorded"}
                  </div>
                  <div style={{ color: T.mid, fontSize: T.fsXs, marginTop: 3 }}>IP: {login.clientIp || "Not recorded"}</div>
                </li>
              ))}
            </ul>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12, color: T.mid, fontSize: T.fsXs }}>
              <span>Page {loginState.data.page} · {loginState.data.total} sign-ins</span>
              <div style={{ display: "flex", gap: 8 }}>
                <button type="button" data-testid="admin-user-login-previous" disabled={loginPage <= 1} onClick={() => setLoginPage((page) => Math.max(1, page - 1))}>Previous</button>
                <button type="button" data-testid="admin-user-login-next" disabled={!loginState.data.hasNext} onClick={() => setLoginPage((page) => page + 1)}>Next</button>
              </div>
            </div>
          </>
        )}
        <h4 style={{ color: T.dark, margin: "28px 0 12px", fontSize: T.fsBase }}>Recent activity</h4>
        {state.loading && <p data-testid="admin-user-activity-loading" style={{ color: T.mid }}>Loading activity…</p>}
        {state.error && <p data-testid="admin-user-activity-error" role="alert" style={{ color: T.red }}>{state.error instanceof AuthError ? "You are not allowed to view this activity." : "Activity could not be loaded."}</p>}
        {!state.loading && !state.error && state.events.length === 0 && <p data-testid="admin-user-activity-empty" style={{ color: T.mid }}>No recent activity captured.</p>}
        {!state.loading && !state.error && state.events.length > 0 && (
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {state.events.map((event, index) => (
              <li key={`${event.occurredAt}-${index}`} style={{ padding: "12px 0", borderBottom: `1px solid ${T.border}` }}>
                <div style={{ color: T.dark, fontSize: T.fsSm, fontWeight: T.fwSemibold }}>
                  {event.event === "login" ? "Signed in" : `Viewed ${event.surface}`}
                </div>
                <div style={{ color: T.mid, fontSize: T.fsXs, marginTop: 4 }}>{formatDate(event.occurredAt)}</div>
              </li>
            ))}
          </ul>
        )}
      </aside>
    </div>
  );
}

export { formatDate as formatAdminUserDate };